import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ConcurLinkedService, concurAuthType } from 'app/model/linkedservice/concur.linkedservice';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { concurReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ConcurLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                clientId: '*',
                username: '*',
                password: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*',
                connectionProperties: {
                    host: '*',
                    authenticationType: '*',
                    baseUrl: '*',
                    clientId: '*',
                    clientSecret: 'secret',
                    username: '*',
                    password: 'secret',
                    useEncryptedEndpoints: '*',
                    useHostVerification: '*',
                    usePeerVerification: '*',
                    encryptedCredential: '*'
                }
            }
        }
    }
})
export class ConcurLinkedServiceAdapter extends LinkedServiceAdapter<ConcurLinkedService> {
    protected modelConstructor(): new () => ConcurLinkedService {
        return ConcurLinkedService;
    }

    protected model(): IModel {
        return ConcurLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: ConcurLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.ConcurLinkedService;
        properties.type = Metamodel.modelFor(ConcurLinkedService).name;

        if (linkedService.isOldResource) {
            properties.typeProperties = {
                clientId: linkedService.clientId,
                username: linkedService.username,
                password: passwordOrAKVtoSecretBase(linkedService.password, this.context),
                useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
            };
        } else {
            properties.typeProperties = {
                connectionProperties: {
                    authenticationType: linkedService.authType,
                    host: linkedService.host,
                    baseUrl: linkedService.baseUrl,
                    clientId: linkedService.clientId,
                    username: linkedService.username,
                    password: passwordOrAKVtoSecretBase(linkedService.password, this.context),
                    useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                    useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                    usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined,
                    clientSecret: passwordOrAKVtoSecretBase(linkedService.password, this.context)
                }
            };

            if (linkedService.authType === concurAuthType.newAuthVersion) {
                properties.typeProperties.connectionProperties.clientSecret = passwordOrAKVtoSecretBase(linkedService.clientSecret, this.context);
            }
            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: ConcurLinkedService): Promise<ConcurLinkedService> {
        const properties = linkedServiceResource.properties as Model.ConcurLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.ConcurLinkedServiceTypeProperties = properties.typeProperties;

        if (typeProperties.connectionProperties) {
            linkedService.isOldResource = false;
            const connectionProperties = typeProperties.connectionProperties;
            linkedService.authType = connectionProperties.authenticationType;
            linkedService.host = connectionProperties.host;
            linkedService.baseUrl = connectionProperties.baseUrl;
            linkedService.clientId = connectionProperties.clientId;
            linkedService.username = connectionProperties.username;
            linkedService.password = secretBaseToPasswordOrAKV(connectionProperties.password, true, this.context);
            linkedService.useEncryptedEndpoints = connectionProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = connectionProperties.useHostVerification;
            linkedService.usePeerVerification = connectionProperties.usePeerVerification;

            if (linkedService.authType === concurAuthType.newAuthVersion) {
                linkedService.clientSecret = secretBaseToPasswordOrAKV(connectionProperties.clientSecret, true, this.context);
            }

            linkedService.additionalFields = [];
            Object.keys(connectionProperties).forEach((key) => {
                if (concurReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            linkedService.clientId = typeProperties.clientId;
            linkedService.username = typeProperties.username;
            linkedService.password = secretBaseToPasswordOrAKV(typeProperties.password, true, this.context);
            linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = typeProperties.useHostVerification;
            linkedService.usePeerVerification = typeProperties.usePeerVerification;
        }

        return linkedService;
    }
}
